<template>
  <SubViewWrapper
    title="URL Column"
    :hide-header="embedded"
    @back="$emit('back')"
    @close="$emit('close')"
  >
    <div class="content" :class="{ embedded }">
      <div class="field-row">
        <span class="label">Column</span>
        <BaseInputSelect
          class="column-picker"
          select-only
          :options="columnOptions"
          :value="selectedValue"
          placeholder="Pick a URL column…"
          :disabled="!columnOptions.length"
          @update:value="selectColumn"
        />
      </div>

      <div v-if="!embedded" class="actions">
        <BaseButton
          kind="main"
          type="primary"
          text="Save"
          :state="canSave ? 'default' : 'disabled'"
          @click="save"
        />
        <BaseButton
          v-if="urlMapping"
          kind="main"
          type="secondary"
          tint="red"
          text="Clear"
          @click="clear"
        />
      </div>
    </div>
  </SubViewWrapper>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref } from "vue";

import type {
  ColumnId,
  FieldWithOrigin,
  GraphDatabase,
} from "@ctrl/controller-rpc";

import BaseButton from "@/components/base/BaseButton.vue";
import BaseInputSelect, {
  type SelectOption,
} from "@/components/base/BaseInputSelect.vue";
import SubViewWrapper from "@/components/views/config/SubViewWrapper.vue";

export default defineComponent({
  name: "UrlMappingConfig",
  components: { BaseButton, BaseInputSelect, SubViewWrapper },
  props: {
    urlMapping: {
      type: String as PropType<ColumnId>,
      required: false,
    },
    database: {
      type: Object as PropType<GraphDatabase>,
      required: true,
    },
    /**
     * Rendered inside a host that owns the chrome (title bar and Save / Clear
     * actions, driven through `canSave`, `save` and `clear`).
     */
    embedded: { type: Boolean, default: false },
  },
  emits: {
    "update:urlMapping": (_: ColumnId | undefined) => true,
    back: () => true,
    close: () => true,
  },
  setup(props, { emit }) {
    const selectedColumnId = ref<ColumnId | null>(props.urlMapping ?? null);

    const isStringField = (field: FieldWithOrigin): boolean => {
      const v = field.value;
      if (v.type === "string") return true;
      if (v.type !== "variant") return false;
      return v.cases.some((c) => c.type === "string");
    };

    const urlColumns = computed<[ColumnId, FieldWithOrigin][]>(() =>
      Object.entries(props.database.type.fields).filter(([, field]) =>
        isStringField(field)
      )
    );

    const columnOptions = computed<SelectOption[]>(() =>
      urlColumns.value.map(([id, field]) => ({
        value: id,
        label: field.name,
        aliases: [],
      }))
    );
    /**
     * The picker's value: empty while the selected column is unknown to the
     * sampled type, so a stale mapping reads as unset.
     */
    const selectedValue = computed<string>(() =>
      columnOptions.value.some((o) => o.value === selectedColumnId.value)
        ? (selectedColumnId.value ?? "")
        : ""
    );

    const selectColumn = (id: ColumnId) => {
      selectedColumnId.value = id;
    };

    const canSave = computed<boolean>(() => selectedValue.value !== "");

    const save = () => {
      const col = selectedColumnId.value;
      if (!col) return;
      emit("update:urlMapping", col);
      emit("back");
    };

    const clear = () => {
      emit("update:urlMapping", undefined);
      emit("back");
    };

    return {
      columnOptions,
      selectedColumnId,
      selectedValue,
      selectColumn,
      canSave,
      save,
      clear,
    };
  },
});
</script>

<style scoped lang="scss">
@use "../../styles/settings.scss";

.content {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;

  &.embedded {
    padding: 0;
  }
}

.field-row {
  display: flex;
  align-items: center;
  gap: 12px;

  .label {
    @extend %font-body-medium;
    flex-shrink: 0;
    width: 64px;
  }

  .column-picker {
    flex: 1;
  }
}

.actions {
  display: flex;
  gap: 8px;
  padding-top: 8px;
}
</style>
